import React from 'react';
import PropTypes from 'prop-types';

import Translate, { useI18n } from '@hydra/common/components/i18n';
import Criterium from '@hydra/common/criterium/Criterium';
import CriteriumChart from '@hydra/common/criterium/CriteriumChart';
import getGraphData from '@hydra/utils/getGraphData';
import ReviewCriteriaCTA from '@hydra/common/components/CTA/ReviewCriteriaCTA';
import { useReviewContext } from '@hydra/common/context/review';
import ReviewSkeleton from '@hydra/common/components/ReviewSkeleton';
import { GoogleAnalyticsDataAuthWrapper } from '@hydra/common/components/GoogleAnalytics/GoogleAnalyticsDataWrapper';

import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  graph: {
    marginTop: theme.spacing(2)
  },
  title: {
    padding: theme.spacing(1, 2),
    fontWeight: theme.typography.fontWeightBold,
    minWidth: 0
  }
}));

const monthlyReport = {
  reportType: 'ga',
  query: {
    metrics: 'ga:sessions, ga:newUsers',
    dimensions: 'ga:yearmonth',
    'end-date': 'today',
    'start-date': '365daysAgo'
  }
};

const dailyReport = {
  reportType: 'ga',
  query: {
    metrics: 'ga:sessions, ga:newUsers',
    dimensions: 'ga:date',
    'end-date': 'today',
    'start-date': '31daysAgo'
  }
};

function getChartData (data, translate) {
  return [
    {
      name: translate('pj_ga_newVisits'),
      data: (data || []).map(([x, y]) => getGraphData({ x, y }))
    },
    {
      name: translate('pj_ga_visits'),
      data: (data || []).map(([x, y, y2]) => getGraphData({ x, y: y2 || 0 }))
    }
  ];
}

function DailyVisitors ({ data }) {
  const { translate } = useI18n();
  const chartData = getChartData(data, translate);
  return (
    <CriteriumChart
      type='line'
      frequency='day'
      hasLegend
      hasTooltip
      data={chartData}
    />
  );
}

function MonthlyVisitors ({ data }) {
  const { translate } = useI18n();
  const chartData = getChartData(data, translate);
  return (
    <CriteriumChart
      type='line'
      frequency='month'
      hasLegend
      hasTooltip
      data={chartData}
    />
  );
}

export const PjGaAudienceVisitors = ({ criterium }) => {
  const classes = useStyles();
  const { isFree } = useReviewContext();

  if (isFree) {
    return (
      <Criterium criterium={{ type: 'pj_ga_audience_visitors', score: null, importance: 0, solvability: 0 }} clickable={false} align='center'>
        <ReviewCriteriaCTA
          text={<Translate component='div' content='pj_ga_audience_visitors_cta_text' />}
          event='sessions'
        />
      </Criterium>
    );
  }

  return (
    <Criterium criterium={criterium || { type: 'pj_ga_audience_visitors', score: null, importance: 0, solvability: 0 }} clickable={!!criterium}>
      <Translate className={classes.title} content='pj_daily_data' />
      <div className={classes.graph}>
        {criterium
          ? (
            <GoogleAnalyticsDataAuthWrapper
              accessToken={criterium.data && criterium.data.accessToken}
              viewId={criterium.data && criterium.data.viewId}
              report={dailyReport}
              Component={DailyVisitors}
              loadingComponent={<ReviewSkeleton height={300} />}
            />
            )
          : <ReviewSkeleton height={300} />}
      </div>
      <Translate className={classes.title} content='pj_monthly_data' />
      <div className={classes.graph}>
        {criterium
          ? (
            <GoogleAnalyticsDataAuthWrapper
              accessToken={criterium.data && criterium.data.accessToken}
              viewId={criterium.data && criterium.data.viewId}
              report={monthlyReport}
              Component={MonthlyVisitors}
              loadingComponent={<ReviewSkeleton height={300} />}
            />
            )
          : <ReviewSkeleton height={300} />}
      </div>
    </Criterium>
  );
};

PjGaAudienceVisitors.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      viewId: PropTypes.string,
      accessToken: PropTypes.string
    })
  })
};

export default React.memo(PjGaAudienceVisitors);
